import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, MutableRefObject, useMemo, useRef, VFC } from 'react';
import styled from 'styled-components';

import HandlebarPill from 'dashboard/components/HandlebarFormControl/HandlebarPill';
import { validatePath } from 'dashboard/components/JsonSchemaSelect';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { parseHandlebarsText } from 'dashboard/lib/utils/handlebars';
import {
  usePlatformObjectJsonSchema,
  usePrimitiveUtilities,
} from 'dashboard/redux/selectors/workflow-editor-context';
import Input, { InputProps } from 'design-system/components/Input';
import TextArea, { TextAreaProps } from 'design-system/components/TextArea';
import inputMixin, { InputMixinProps } from 'design-system/mixins/input';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema } from 'lib/json-schema';

const FormControlWrapper = styled.div<{ visible: boolean }>`
  width: 100%;
  position: absolute;
  color: ${({ visible }) => (visible ? 'initial' : 'transparent')};

  & > input {
    /* make a space for the + button (positioned absolute) to sit in */
    padding-right: ${Spacing[6]};
  }
`;

const PillsInputWrapper = styled.div<InputMixinProps & { visible: boolean }>`
  ${inputMixin}
  position: relative;
  white-space: pre;
  pointer-events: none;
  overflow: hidden;
  visibility: ${({ visible }) => (visible ? 'initial' : 'hidden')};
  padding-right: ${Spacing[5]};
  padding-top: 6px;
`;

const PillsTextAreaWrapper = styled.div<InputMixinProps & { visible: boolean }>`
  ${inputMixin};
  position: relative;
  white-space: pre-wrap;
  word-break: break-word;
  pointer-events: none;
  padding-bottom: 0;
  height: ${({ theme }) => theme.element.height.xl};
  overflow: auto;
  visibility: ${({ visible }) => (visible ? 'initial' : 'hidden')};
  padding-right: ${Spacing[5]};
`;

const CodeContainer = styled.div`
  height: 100px;

  & .cm-editor {
    padding-right: 32px;
  }
`;

type FormControlType = InputProps & TextAreaProps;

export interface FormControlProps extends FormControlType {
  allowedFieldPaths: string[];
  editable: boolean;
  pillsEditable?: boolean;
  jsonSchema: JsonSchema;
  textRef: MutableRefObject<HTMLInputElement & HTMLTextAreaElement>;
  type: 'input' | 'text-area' | 'code';
  value: string;
  onUpdateCursorOnCode: (pos: number) => void;
  onValueChange: (value: string) => void;
}

const FormControl: VFC<FormControlProps> = ({
  allowedFieldPaths,
  editable,
  pillsEditable,
  jsonSchema,
  textRef,
  type,
  value,
  onUpdateCursorOnCode,
  onValueChange,
  ...otherProps
}) => {
  const platformObjectJsonSchema = usePlatformObjectJsonSchema();
  const primitiveUtilities = usePrimitiveUtilities();

  const pillsRef = useRef<HTMLDivElement>(null);
  const [active, { enable: activate, disable: deactivate }] = useToggle(false);
  // wraps handlebars in a string with HandlebarPill
  const pills = useMemo(() => {
    if (active) return value;

    try {
      return parseHandlebarsText(value).map(({ content, variableName: word, position }, index) => {
        if (word == null) {
          return content;
        } else {
          const { valid } = validatePath({
            path: word,
            jsonSchema,
            platformObjectJsonSchema,
            primitiveUtilities,
            optionFilters: {
              allowedFieldPaths,
            },
          });
          const onDelete = () => {
            const valueWithoutHandlebarWord =
              value.slice(0, position.start) + value.slice(position.end + 1);
            onValueChange(valueWithoutHandlebarWord);
          };

          return (
            <HandlebarPill
              key={index}
              value={word}
              error={!valid}
              onDelete={(pillsEditable ?? editable) ? onDelete : undefined}
            />
          );
        }
      });
    } catch {
      return value;
    }
  }, [
    active,
    value,
    jsonSchema,
    platformObjectJsonSchema,
    primitiveUtilities,
    allowedFieldPaths,
    pillsEditable,
    editable,
    onValueChange,
  ]);

  const formControlProps = {
    ref: textRef,
    value: value,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onValueChange(e.target.value);
    },
    visible: active,
    disabled: !editable,
    onFocus: activate,
    onBlur: deactivate,
    onScroll: () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pillsRef.current.scrollTop = textRef.current.scrollTop;
    },
    style: { paddingRight: '24px' },
    ...otherProps,
  };

  const pillsProps = {
    ref: pillsRef,
    disabled: !(pillsEditable ?? editable),
    visible: !active && value !== '',
    style: otherProps.style || {},
  };

  switch (type) {
    case 'input':
      return (
        <>
          <FormControlWrapper visible={active}>
            <Input {...formControlProps} />
          </FormControlWrapper>
          <PillsInputWrapper $size='md' {...pillsProps}>
            {pills}
          </PillsInputWrapper>
        </>
      );
    case 'text-area':
      return (
        <>
          <FormControlWrapper visible={active}>
            <TextArea {...formControlProps} />
          </FormControlWrapper>
          <PillsTextAreaWrapper $size='md' {...pillsProps}>
            {pills}
          </PillsTextAreaWrapper>
        </>
      );
    case 'code':
      return (
        <CodeContainer data-test='form-control__code'>
          <CodeEditor
            language='json'
            code={value}
            readOnly={!editable}
            onChange={onValueChange}
            onUpdate={(viewUpdate) => {
              onUpdateCursorOnCode(viewUpdate.state.selection.main.head);
            }}
          />
        </CodeContainer>
      );
  }
};

export default FormControl;
